import type { CSSProperties } from "react";
import { ArrowRightIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import { BAND_ACTION, BAND_HEADLINE, BAND_LINE, FLOW, FLOW_TITLE } from "@/lib/landing";
import { ROUTES } from "@/lib/routes";
import { useGate } from "./gateContext";
import PlaceLink from "./PlaceLink";
import { INK_BUTTON } from "./styles";

/** Four steps from a saved resume to a reviewed application, shown to visitors. */
export function OnePosting() {
    return (
        <section aria-labelledby="flow-title" className="mb-2 mt-5 flex flex-col gap-4">
            <h2
                id="flow-title"
                className="m-0 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.03em]"
            >
                {FLOW_TITLE}
            </h2>
            <ol className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-3 p-0">
                {FLOW.map((step, index) => {
                    const last = index === FLOW.length - 1;
                    return (
                        <li
                            key={step.label}
                            className={`flow-step flex flex-col gap-2 rounded-2xl px-5 py-[18px] ${
                                last ? "gradient-frame" : "border border-line bg-card"
                            }`}
                            style={{ "--step": index } as CSSProperties}
                        >
                            <span className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.04em] text-brand-ink">
                                <span className="grid size-5 place-items-center rounded-md bg-grad text-[11px] text-brand-fg">
                                    {index + 1}
                                </span>
                                {step.label}
                            </span>
                            <span className="font-display text-[18px] font-bold tracking-[-0.015em]">
                                {step.headline}
                            </span>
                            <span className="text-[13px] leading-normal text-fg-muted [text-wrap:pretty]">
                                {step.line}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

/** The closing invitation opens the existing free-account sheet. */
export function ClosingBand() {
    const { ask } = useGate();
    return (
        <section
            aria-labelledby="band-title"
            className="mt-3 flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-grad px-10 py-9 text-brand-fg shadow-glow max-[760px]:px-[22px] max-[760px]:py-7"
        >
            <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-1.5">
                <h2
                    id="band-title"
                    className="m-0 font-display text-[32px] font-extrabold leading-[1.08] tracking-[-0.03em] [text-wrap:balance] max-[760px]:text-[26px]"
                >
                    {BAND_HEADLINE}
                </h2>
                <p className="m-0 max-w-[60ch] text-[14.5px] opacity-85 [text-wrap:pretty]">
                    {BAND_LINE}
                </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
                <button
                    {...actionProps("join_signup_clicked", { source: "ClosingBand" })}
                    type="button"
                    onClick={() => ask("join")}
                    className={`${INK_BUTTON} h-[46px] rounded-[13px] px-5 text-[14px] hover:-translate-y-px`}
                >
                    {BAND_ACTION}
                    <ArrowRightIcon className="size-[15px]" />
                </button>
                <PlaceLink
                    {...actionProps("navigation_clicked", {
                        source: "ClosingBand",
                        target: ROUTES.PRICING,
                    })}
                    to={ROUTES.PRICING}
                    className="press inline-flex h-[46px] items-center rounded-[13px] border-[1.5px] border-[rgba(20,32,26,.35)] px-[18px] text-[14px] font-semibold text-brand-fg transition-[background-color,transform] hover:bg-[rgba(20,32,26,.06)]"
                >
                    See the plans
                </PlaceLink>
            </div>
        </section>
    );
}
